import React from 'react';
import { RenderNode } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';

import { Text } from '../Text';
import { Box } from '../Box';
import RichText from '../RichText';
import { EmbeddedInlineEntryNode, HyperlinkNode } from '../RichText/Nodes';
import { BlockContainer, Layout, Column } from '../Layout';
import { ImageAsset } from './ImageAsset';
import { VideoAsset } from './VideoAsset';

import { TextWrapper, StyledUnorderedList, UnorderedList } from './styles';
import { IAssetWith5050TextFields } from '../../../types/contentful';

interface Props extends IAssetWith5050TextFields {
  sysId?: string;
}

export function AssetWith5050Text({
  headingText,
  bodyCopy,
  asset,
  assetTitle,
  assetFirst,
  vimeoId,
  roundedCorners,
  checkmarkBullets,
  sysId
}: Props) {
  const RICH_TEXT_OVERRIDE: RenderNode = {
    [BLOCKS.UL_LIST]: (_node, children) =>
      checkmarkBullets ? (
        <StyledUnorderedList>{children}</StyledUnorderedList>
      ) : (
        <UnorderedList>{children}</UnorderedList>
      ),
    [INLINES.HYPERLINK]: (node, children) => (
      <HyperlinkNode node={node} colour="inlineAnchor">
        {children}
      </HyperlinkNode>
    ),
    [INLINES.EMBEDDED_ENTRY]: (node, children) => (
      <EmbeddedInlineEntryNode
        node={node}
        colour="inlineAnchor"
        fontWeight="inlineAnchor"
      >
        {children}
      </EmbeddedInlineEntryNode>
    )
  };

  return (
    <BlockContainer
      xPaddingOverrides={{ 0: 0, min: 0, mobile: 0 }}
      marginTop={{ 0: 'blockMarginY_tablet', desktop: 'blockMarginY_desktop' }}
      marginBottom={{
        0: 'blockMarginY_tablet',
        desktop: 'blockMarginY_desktop'
      }}
      data-elementtype="assetWith5050Text"
      data-elementid={sysId}
      data-testid="assetWith5050Text"
    >
      <Layout
        gutter={{ 0: 0, tablet: 'm' }}
        flexWrap="wrap"
        alignItems="center"
        flexDirection={{
          0: 'column',
          tablet: assetFirst ? 'row' : 'row-reverse'
        }}
      >
        <Column
          size={{ 0: 12, tablet: 7 }}
          padding={
            roundedCorners
              ? { 0: 'sm', tablet: [0, 'sm'] }
              : { 0: ['sm', 0], tablet: [0, 'sm'] }
          }
        >
          {vimeoId ? (
            <VideoAsset
              asset={asset}
              title={assetTitle}
              vimeoId={vimeoId}
              rounded={roundedCorners ?? false}
            />
          ) : (
            <ImageAsset
              asset={asset}
              title={assetTitle}
              rounded={roundedCorners ?? false}
            />
          )}
        </Column>
        <Column
          size={{ 0: 10, tablet: 4 }}
          marginTop={{ 0: 'ml', tablet: 0 }}
          paddingLeft="sm"
          paddingRight="sm"
        >
          <TextWrapper id="text-wrapper">
            <Box marginBottom={{ 0: 'sm' }}>
              <Text
                as="h2"
                fontWeight={{ 0: 'h2' }}
                fontSize={{ 0: 'h2_0', desktop: 'h2_desktop' }}
                data-blockid="assetWith5050Text-headline"
                data-testid="assetWith5050Text-headline"
              >
                {headingText}
              </Text>
            </Box>
            {bodyCopy && (
              <Box data-blockid="assetWith5050Text-subhead">
                <RichText
                  data={bodyCopy}
                  overrideOptions={RICH_TEXT_OVERRIDE}
                />
              </Box>
            )}
          </TextWrapper>
        </Column>
        <Column size={1} />
      </Layout>
    </BlockContainer>
  );
}
